import React, { useState, useCallback, useMemo, useEffect, useRef, useLayoutEffect } from 'react'
import type { DemoContent } from '../editor/types'
import BlockRenderer from './BlockRenderer'
import { groupBlockRows } from './groupBlockRows'
import { evaluateVisibility } from '../lib/visibility'
import { useFormulaEvaluation } from '../lib/useFormulaEvaluation'
import { loadGoogleFont, getFontFamily } from '../editor/components/ThemeEditor'

const isEmbedded = typeof window !== 'undefined' && window.self !== window.top

function isDarkBackground(background?: DemoContent['background']): boolean {
  if (!background) return false
  const val = background.value || ''
  if (background.type === 'solid') {
    // Parse hex color and check luminance
    const hex = val.replace('#', '')
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16)
      const g = parseInt(hex.slice(2, 4), 16)
      const b = parseInt(hex.slice(4, 6), 16)
      return (r * 299 + g * 587 + b * 114) / 1000 < 128
    }
  }
  if (background.type === 'animated') {
    return val.includes('dark') || val.includes('cyber') || val.includes('electric') || val.includes('noir')
  }
  return false
}

interface RendererAppProps {
  content: DemoContent
  demoId?: number
}

function getBackgroundStyle(background?: DemoContent['background']): React.CSSProperties {
  if (!background) return {}
  switch (background.type) {
    case 'solid':
      return { backgroundColor: background.value }
    case 'gradient':
      return { background: background.value }
    case 'animated':
      return {} // handled by className
    case 'image':
      return { backgroundImage: `url(${background.value})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    default:
      return {}
  }
}

function getBackgroundClass(background?: DemoContent['background']): string {
  if (!background || background.type !== 'animated') return ''
  return background.value // CSS class name like 'bg-animated-waves'
}

export default function RendererApp({ content, demoId }: RendererAppProps) {
  const [variableValues, setVariableValues] = useState<Record<string, number | string | boolean>>(() => {
    const vals: Record<string, number | string | boolean> = {}
    content.variables.forEach((v) => {
      vals[v.name] = v.default
    })
    return vals
  })

  const setVariableValue = useCallback((name: string, value: number | string | boolean) => {
    setVariableValues((prev) => ({ ...prev, [name]: value }))
  }, [])

  // Evaluate formula variables
  useFormulaEvaluation(content.variables, variableValues, setVariableValue)

  const accent = content.theme?.accent || '#5048e5'
  const bgStyle = getBackgroundStyle(content.background)
  const bgClass = getBackgroundClass(content.background)
  const fontFamily = getFontFamily(content.theme?.font)

  useEffect(() => {
    if (content.theme?.font) loadGoogleFont(content.theme.font)
  }, [content.theme?.font])

  // Fade-in after mount to avoid content flash
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    requestAnimationFrame(() => setMounted(true))
  }, [])

  // Step visibility: filter blocks controlled by steps blocks
  const visibleBlocks = useMemo(() => {
    const controlled = new Set<string>()
    const visible = new Set<string>()
    for (const block of content.blocks) {
      if (block.type !== 'steps' || !block.config) continue
      const cfg = block.config as Record<string, unknown>
      const groups = cfg.stepGroups as Record<string, string[]> | undefined
      if (!groups) continue
      const currentStep = Number(block.variable ? variableValues[block.variable] ?? 0 : 0)
      for (const [stepIdx, blockIds] of Object.entries(groups)) {
        for (const bid of blockIds) {
          controlled.add(bid)
          if (Number(stepIdx) === currentStep) {
            visible.add(bid)
          }
        }
      }
    }
    return content.blocks
      .filter((b) => !controlled.has(b.id) || visible.has(b.id))
      .filter((b) => evaluateVisibility(b.visibility, variableValues))
  }, [content.blocks, variableValues])

  const rows = useMemo(() => groupBlockRows(visibleBlocks), [visibleBlocks])

  // Auto-resize iframe: notify parent of content height changes
  const contentRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!isEmbedded || !contentRef.current) return
    let lastH = 0
    let timer: ReturnType<typeof setTimeout>
    const notify = () => {
      const h = Math.ceil(contentRef.current!.offsetHeight + contentRef.current!.offsetTop + 20)
      if (h > 50 && Math.abs(h - lastH) > 2) {
        lastH = h
        window.parent.postMessage({ type: 'explaina-resize', height: h, src: window.location.pathname }, '*')
      }
    }
    const debounced = () => { clearTimeout(timer); timer = setTimeout(notify, 100) }
    const ro = new ResizeObserver(debounced)
    ro.observe(contentRef.current)
    // Initial notify after paint
    setTimeout(notify, 200)
    return () => { ro.disconnect(); clearTimeout(timer) }
  }, [rows])

  return (
    <div
      ref={contentRef}
      className={`${isEmbedded ? '' : 'min-h-screen'} ${bgClass} transition-opacity duration-300 ${mounted ? 'opacity-100' : 'opacity-0'}`}
      style={{ ...bgStyle, '--accent': accent, '--block-text': isDarkBackground(content.background) ? '#f1f5f9' : '#0f172a', ...(fontFamily ? { fontFamily } : {}) } as React.CSSProperties}
    >
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-3 sm:px-6 sm:py-8 sm:space-y-4">
        {rows.map((row) => {
          const isSingleFull = row.length === 1 && (row[0].span || 3) === 3
          if (isSingleFull) {
            return (
              <div key={row[0].id}>
                <BlockRenderer
                  block={row[0]}
                  variableValues={variableValues}
                  variables={content.variables}
                  onSetVariableValue={setVariableValue}
                  demoId={demoId}
                />
              </div>
            )
          }
          return (
            <div key={row.map((b) => b.id).join('-')} className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4 items-stretch">
              {row.map((block) => (
                <div key={block.id} style={{ gridColumn: `span ${block.span || 1}` }}>
                  <BlockRenderer
                    block={block}
                    variableValues={variableValues}
                    variables={content.variables}
                    onSetVariableValue={setVariableValue}
                    demoId={demoId}
                  />
                </div>
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}
